/**
 * OnboardingProgress Component
 *
 * Displays a 10-step vertical timeline showing live tenant onboarding progress.
 * Polls the onboarding status endpoint and updates step states in real time.
 *
 * WQ-127: Tenant Onboarding Wizard
 */

import React from 'react';

interface OnboardingStep {
  step: number;
  name: string;
  status: 'pending' | 'in_progress' | 'success' | 'failed' | 'skipped';
  message?: string;
}

interface OnboardingProgressProps {
  tenantName: string;
  steps: OnboardingStep[];
  isComplete: boolean;
  isFailed: boolean;
  adminTempPassword?: string;
  onRetry: () => void;
  onClose: () => void;
}

const STEP_DEFINITIONS: Array<{ step: number; name: string }> = [
  { step: 1, name: 'Create tenant record' },
  { step: 2, name: 'Assign template' },
  { step: 3, name: 'Provision infrastructure' },
  { step: 4, name: 'Assign backup policy' },
  { step: 5, name: 'Create admin user' },
  { step: 6, name: 'Configure SSO defaults' },
  { step: 7, name: 'Assign feature flags' },
  { step: 8, name: 'Configure monitoring' },
  { step: 9, name: 'Register version' },
  { step: 10, name: 'Activate tenant' },
];

function getStatusIcon(status: OnboardingStep['status']): string {
  switch (status) {
    case 'success': return '\u2713';
    case 'failed': return '\u2717';
    case 'skipped': return '\u2014';
    case 'in_progress': return '\u25CB';
    default: return '\u25CB';
  }
}

function getStatusColor(status: OnboardingStep['status']): string {
  switch (status) {
    case 'success': return '#22c55e';
    case 'failed': return '#ef4444';
    case 'skipped': return '#f59e0b';
    case 'in_progress': return '#3b82f6';
    default: return '#6b7280';
  }
}

export const OnboardingProgress: React.FC<OnboardingProgressProps> = ({
  tenantName,
  steps,
  isComplete,
  isFailed,
  adminTempPassword,
  onRetry,
  onClose,
}) => {
  const [copied, setCopied] = React.useState(false);

  // Build merged step list: definitions + actual status
  const mergedSteps = STEP_DEFINITIONS.map((def) => {
    const actual = steps.find((s) => s.step === def.step);
    return {
      ...def,
      status: def.step === 1 ? 'success' as const : (actual?.status || 'pending' as const),
      message: def.step === 1 ? 'Tenant record created' : (actual?.message || ''),
    };
  });

  const handleCopy = () => {
    if (adminTempPassword) {
      navigator.clipboard.writeText(adminTempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>
        Onboarding: {tenantName}
      </h3>

      {/* Step timeline */}
      <div style={{ position: 'relative', paddingLeft: '32px' }}>
        {mergedSteps.map((step, idx) => (
          <div
            key={step.step}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: idx < mergedSteps.length - 1 ? '8px' : 0,
              position: 'relative',
            }}
          >
            {/* Connector line */}
            {idx < mergedSteps.length - 1 && (
              <div
                style={{
                  position: 'absolute',
                  left: '-20px',
                  top: '20px',
                  width: '2px',
                  height: '28px',
                  background: step.status === 'success' ? '#22c55e' : '#374151',
                }}
              />
            )}

            {/* Step circle */}
            <div
              style={{
                position: 'absolute',
                left: '-28px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 600,
                color: '#fff',
                background: getStatusColor(step.status),
                flexShrink: 0,
              }}
            >
              {step.status === 'in_progress' ? (
                <span style={{ animation: 'spin 1s linear infinite' }}>&#8635;</span>
              ) : (
                getStatusIcon(step.status)
              )}
            </div>

            {/* Step content */}
            <div style={{ flex: 1, minHeight: '28px' }}>
              <div style={{
                fontSize: '13px',
                fontWeight: step.status === 'in_progress' ? 600 : 400,
                color: step.status === 'pending' ? '#6b7280' : '#e5e7eb',
              }}>
                {step.name}
              </div>
              {step.message && step.status !== 'pending' && (
                <div style={{
                  fontSize: '11px',
                  color: step.status === 'failed' ? '#fca5a5' : '#9ca3af',
                  marginTop: '2px',
                }}>
                  {step.message}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Success banner */}
      {isComplete && (
        <div style={{
          marginTop: '20px',
          padding: '12px 16px',
          background: 'rgba(34, 197, 94, 0.15)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '8px',
        }}>
          <div style={{ fontWeight: 600, color: '#22c55e', marginBottom: '8px' }}>
            Tenant onboarding complete
          </div>
          {adminTempPassword && (
            <div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                Admin temporary password (shown once):
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <code style={{
                  padding: '4px 8px',
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: 'monospace',
                  color: '#e5e7eb',
                }}>
                  {adminTempPassword}
                </code>
                <button
                  className="btn btn-sm"
                  onClick={handleCopy}
                  style={{ fontSize: '11px', padding: '2px 8px' }}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Failure banner */}
      {isFailed && (
        <div style={{
          marginTop: '20px',
          padding: '12px 16px',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
        }}>
          <div style={{ fontWeight: 600, color: '#ef4444', marginBottom: '8px' }}>
            Onboarding failed
          </div>
          <div style={{ fontSize: '12px', color: '#fca5a5' }}>
            {steps.find(s => s.status === 'failed')?.message || 'Unknown error'}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ marginTop: '20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        {isFailed && (
          <button className="btn btn-primary" onClick={onRetry}>
            Retry
          </button>
        )}
        <button className="btn btn-ghost" onClick={onClose}>
          {isComplete ? 'Close' : 'Cancel'}
        </button>
      </div>
    </div>
  );
};
